import { useState, useEffect } from 'react';
import { applyTheme, getCurrentTheme } from '@/lib/colors';

const THEME_STORAGE_KEY = 'fastenai-theme';

/**
 * Custom hook to manage theme state with localStorage persistence
 * @returns {Object} { theme, toggleTheme }
 */
export function useTheme() {
  // Initialize theme from localStorage or default to 'light'
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    return 'light'; // Default to light mode
  });

  // Apply theme to DOM and sync on mount/change
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    
    // Sync with DOM in case it was changed elsewhere
    const currentDomTheme = getCurrentTheme();
    if (currentDomTheme !== theme) {
      applyTheme(theme);
    }
  }, [theme]);

  // Listen for storage changes (e.g., from other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === THEME_STORAGE_KEY && e.newValue) {
        const newTheme = e.newValue === 'dark' ? 'dark' : 'light';
        setTheme(newTheme);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => {
      const newTheme = prev === 'dark' ? 'light' : 'dark';
      return newTheme;
    });
  };

  return { theme, toggleTheme };
}

